import React, { useContext, useEffect, useState } from 'react'
import API from '../../../../services/API'
import Autocomplete from '@mui/material/Autocomplete'

import {
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import WorkspacesIcon from '@mui/icons-material/Workspaces'
import { GlobalContext, TenantSettingsContext } from '../../../../App'
import UnsavedChanges from '../../../../components/UnsavedChanges/UnsavedChanges'
import CategoryTable from '../VDI/CategoryTable'
import SmallModal from '../../../../components/Modals/SmallModal'
import EditCategory from '../VDI/EditCategory'
import AddField from '../VDI/AddField'

const VehicleCleaning = () => {
  const [data, setData] = useState({})
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState([])
  const [fetchError, setFetchError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [changesPresent, setChangesPresent] = useState(false)
  const [addCategory, setAddCategory] = useState({})
  const [selectedCategory, setSelectedCategory] = useState('')
  const [deepCleanExemptTypes, setDeepCleanExemptTypes] = useState([])

  const [deepCleanFrequency, setDeepCleanFrequency] = useState(12)

  const [editCategory, setEditCategory] = useState('')
  const { setToast } = useContext(GlobalContext)
  const { setOrgConfig } = useContext(TenantSettingsContext)

  // Everything on this page - including the deep clean frequency - comes from
  // this one fetch. The app-wide settings context is only loaded at start-up,
  // so seeding from it could send a stale frequency back on the next save.
  useEffect(() => {
    API.get('/vehiclecleaningoptions')
      .then((res) => {
        if (res.status === 200) {
          setData(res.data.vehicleCleaningOptions ?? {})
          setVehicleTypeOptions(res.data.vehicleTypes ?? [])
          setDeepCleanExemptTypes(res.data.deepCleanExemptTypes || [])
          setDeepCleanFrequency(res.data.deepCleanFrequency ?? 12)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  const staleTypes = loading
    ? []
    : [
        ...new Set(
          Object.values(data ?? {})
            .flat()
            .flatMap((item) => item?.vehicleType ?? [])
            .filter((type) => !vehicleTypeOptions.includes(type)),
        ),
      ]

  // Edits only live in this page until saved - warn before a reload or close.
  useEffect(() => {
    if (!changesPresent) return undefined
    const handler = (e) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [changesPresent])

  const handleSaveChanges = () => {
    if (loading || fetchError) return
    API.post('/vehiclecleaningoptions', {
      vehicleCleaningOptions: data,
      deepCleanFrequency,
      deepCleanExemptTypes,
    }).then((res) => {
      if (res.status === 200) {
        setChangesPresent(false)
        setToast({ message: 'Changes Saved' })
        setOrgConfig((prev) => ({
          ...prev,
          orgSettings: {
            ...prev?.orgSettings,
            deepCleanFrequency,
            deepCleanExemptTypes,
          },
        }))
      } else {
        setToast({
          message: 'Unable to save changes. Please try again later.',
          severity: 'error',
        })
      }
    })
  }

  return (
    <Stack gap={1}>
      <Stack>
        <Typography variant="title2">Vehicle Cleaning Settings</Typography>
        {fetchError && (
          <Typography variant="body2" color="error">
            Unable to load your cleaning settings. Refresh the page to try
            again - changes can't be saved until they have loaded.
          </Typography>
        )}
      </Stack>
      <Stack gap={2} divider={<Divider />}>
        <Stack gap={1}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: '#374151',
              borderLeft: '4px solid #10b981',
              pl: 1.5,
            }}
          >
            Deep Clean Frequency
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Deep Clean Frequency</InputLabel>
            <Select
              value={deepCleanFrequency}
              disabled={loading}
              onChange={(e) => {
                setDeepCleanFrequency(e.target.value)
                setChangesPresent(true)
              }}
              label="Deep Clean Frequency"
            >
              <MenuItem value={2}>2 weeks</MenuItem>
              <MenuItem value={4}>4 weeks</MenuItem>
              <MenuItem value={6}>6 weeks</MenuItem>
              <MenuItem value={8}>8 weeks</MenuItem>
              <MenuItem value={10}>10 weeks</MenuItem>
              <MenuItem value={12}>12 weeks</MenuItem>
              <MenuItem value={14}>14 weeks</MenuItem>
              <MenuItem value={16}>16 weeks</MenuItem>
              <MenuItem value={18}>18 weeks</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        {/* Section: Deep Clean Exempt Types */}
        <Stack spacing={1.5}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: '#374151',
              borderLeft: '4px solid #10b981',
              pl: 1.5,
            }}
          >
            Deep Clean Exempt Types
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Select the vehicle types that{' '}
            <strong>do not require deep cleans</strong>. These will be excluded
            from scheduling and overdue calculations.
          </Typography>

          <Autocomplete
            value={deepCleanExemptTypes}
            options={vehicleTypeOptions}
            multiple
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Choose exempt types..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            onChange={(e, newExempt) => {
              setDeepCleanExemptTypes(newExempt)
              // updateSettings(vehicleTypes, newExempt)
              setChangesPresent(true)
            }}
            loading={loading}
          />
        </Stack>
        <Stack gap={1}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: '#374151',
              borderLeft: '4px solid #10b981',
              pl: 1.5,
            }}
          >
            Deep Clean Form Contents
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Use this area to customise the parameters of your deep clean form.
            Edits to fields and categories are applied here first - use{' '}
            <strong>Save Changes</strong> in the bar that appears at the bottom
            of the page to keep them.
          </Typography>
          {staleTypes.length > 0 && (
            <Typography variant="body2" sx={{ color: 'warning.main' }}>
              Some fields still refer to vehicle types that no longer exist (
              {staleTypes.join(', ')}). These are crossed out below - edit the
              fields to pick the current vehicle types.
            </Typography>
          )}
          <Stack direction="row" justifyContent="space-between" marginY={2}>
            <Stack direction="row">
              <Button
                onClick={() =>
                  setAddCategory((prev) => ({ ...prev, open: true }))
                }
                startIcon={<WorkspacesIcon />}
                color="anBlue"
                variant="contained"
              >
                Add a Category
              </Button>
            </Stack>
          </Stack>
          <Stack gap={5}>
            {Object.keys(data).map((category) => (
              <CategoryTable
                key={category}
                isVehicleCleans
                category={category}
                setEditCategory={setEditCategory}
                setSelectedCategory={setSelectedCategory}
                data={data}
                setData={setData}
                setChangesPresent={setChangesPresent}
                vehicleTypeOptions={vehicleTypeOptions}
              />
            ))}
          </Stack>
        </Stack>
      </Stack>

      <SmallModal
        open={!!addCategory.open}
        onClose={() => setAddCategory((prev) => ({ ...prev, open: false }))}
      >
        <Stack
          component="form"
          onSubmit={(e) => {
            e.preventDefault()
            if (Object.keys(data).includes(addCategory.value)) {
              setAddCategory((prev) => ({ ...prev, dupError: true }))
            } else {
              setData((prev) => ({ ...prev, [addCategory.value]: [] }))
              setChangesPresent(true)
              setAddCategory({})
            }
          }}
          gap={1}
          padding={0.5}
        >
          <TextField
            value={addCategory.value ?? ''}
            autoComplete="off"
            required
            error={addCategory.dupError}
            helperText={addCategory.dupError && 'This Category already exists'}
            onChange={(e) =>
              setAddCategory((prev) => ({ ...prev, value: e.target.value }))
            }
            label="Category Name"
          />
          <Button
            type="submit"
            variant="contained"
            color="success"
            size="small"
          >
            Submit
          </Button>
        </Stack>
      </SmallModal>
      <EditCategory
        data={data}
        setData={setData}
        category={editCategory}
        onClose={() => setEditCategory('')}
        setChangesPresent={setChangesPresent}
      />
      <AddField
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        data={data}
        setData={setData}
        setChangesPresent={setChangesPresent}
        vehicleTypeOptions={vehicleTypeOptions}
        isVehicleCleans
      />
      <UnsavedChanges
        changesPresent={changesPresent}
        handleSave={handleSaveChanges}
      />
    </Stack>
  )
}

export default VehicleCleaning
